import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
	useGetTopUpRateQuery,
	useUpdateTopUpRateMutation,
} from '../../../store/api/settings.api';
import styles from './SettingsAdminPage.module.css';

function parseRateInput(value) {
	const normalized = String(value).trim().replace(',', '.');
	if (!/^(?:\d+|\d*\.\d{1,4})$/.test(normalized)) return null;

	const rate = Number(normalized);
	if (!Number.isFinite(rate) || rate <= 0 || rate > 999999.9999) return null;
	return rate;
}

export const SettingsAdminPage = () => {
	const navigate = useNavigate();
	const { data, isLoading, isError } = useGetTopUpRateQuery();
	const [updateTopUpRate, { isLoading: isSaving }] = useUpdateTopUpRateMutation();
	const [rateInput, setRateInput] = useState('');
	const [resultMessage, setResultMessage] = useState('');
	const [errorMessage, setErrorMessage] = useState('');

	useEffect(() => {
		if (data?.rateRubPerTl !== undefined) {
			setRateInput(String(data.rateRubPerTl));
		}
	}, [data]);

	const parsedRate = useMemo(() => parseRateInput(rateInput), [rateInput]);
	const exampleTl = parsedRate ? (1000 / parsedRate).toFixed(2) : '—';

	const handleSave = async event => {
		event.preventDefault();
		setResultMessage('');
		setErrorMessage('');

		if (parsedRate === null) {
			setErrorMessage('Введите положительное число с максимум четырьмя знаками после запятой.');
			return;
		}

		const oldRate = data?.rateRubPerTl;
		if (!window.confirm(`Изменить курс с ${oldRate} на ${parsedRate} ₽ за 1 TL?`)) {
			return;
		}

		try {
			const updated = await updateTopUpRate(parsedRate).unwrap();
			setRateInput(String(updated.rateRubPerTl));
			setResultMessage(`Курс сохранён: ${updated.rateRubPerTl} ₽ за 1 TL.`);
		} catch (error) {
			setErrorMessage(error?.data?.message || 'Не удалось сохранить курс.');
		}
	};

	if (isLoading) return <div className={styles.state}>Загрузка настройки…</div>;
	if (isError) return <div className={styles.state}>Не удалось загрузить настройку.</div>;

	return (
		<div className={styles.main}>
			<button className={styles.backButton} onClick={() => navigate('/admin')}>
				← Назад
			</button>

			<div className={styles.card}>
				<h2>Настройки пополнения PS</h2>
				<p className={styles.description}>
					Сумма корзины в ₽ делится на этот курс. Курс влияет только на подбор номинала;
					 цена оплаты берётся из товаров PS_keys.
				</p>

				<form onSubmit={handleSave}>
					<label className={styles.label} htmlFor="top-up-rate">
						₽ за 1 TL
					</label>
					<input
						id="top-up-rate"
						className={styles.input}
						type="number"
						min="0.0001"
						max="999999.9999"
						step="0.0001"
						value={rateInput}
						onChange={event => {
							setRateInput(event.target.value);
							setResultMessage('');
							setErrorMessage('');
						}}
					/>

					<div className={styles.example}>
						Пример: 1000 ₽ ÷ {parsedRate ?? '—'} = {exampleTl} TL
					</div>

					<button className={styles.saveButton} type="submit" disabled={isSaving}>
						{isSaving ? 'Сохраняем…' : 'Сохранить курс'}
					</button>
				</form>

				{resultMessage && <p className={styles.success}>{resultMessage}</p>}
				{errorMessage && <p className={styles.error}>{errorMessage}</p>}
			</div>
		</div>
	);
};
